function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/`;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

function reportInputValidity(input, errorBlock) {
  if (!input || !errorBlock) return false;

  const type = input.type;
  const value = input.value.trim();
  let isValid = true;
  let errorType = "empty";

  isValid = value === "" ? false : true;

  switch (type) {
    case "email":
      if (value !== "" && !input.checkValidity()) {        
        isValid = false;
        errorType = "email"
      }
      break;
  }

  if (isValid) {
    input.removeAttribute("aria-invalid");
    errorBlock.innerText = "";
  } else {
    input.setAttribute("aria-invalid", true);
    errorBlock.innerText = window.errorStrings[errorType];
  }

  return isValid;
}

function togglePageScroll() {
  if (document.documentElement.style.overflow == "hidden") {
    document.documentElement.style.overflow = null;
  } else {
    document.documentElement.style.overflow = "hidden";
  }
}

// --- SECTIONS --- //

class Header {
  constructor(root) {
    this.root = root;
    this.mobileMenu = this.root.querySelector("#HeaderMobileMenu");
    this.localization = this.root.querySelector("#HeaderLocalization");
    this.predictiveSearch = this.root.querySelector("#HeaderPredictiveSearch");
    this.searchInput = this.root.querySelector("#HeaderSearchInput");
    this.searchResults = this.root.querySelector("#HeaderSearchResults");
    this.allAnnouncementsHeight = 0;
    this.isLoading = false;

    this.root.addEventListener('click', this.onClick.bind(this));

    this.calcHeader();
    window.addEventListener('scroll', () => {
      this.root.classList.toggle('sticky', window.scrollY >= this.allAnnouncementsHeight);
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('input', debounce((e) => {
        this.onPredictiveChange(e.target.value);
      }, 500).bind(this));
    }

    document.addEventListener('click', this.onDocumentClick.bind(this));
  }

  onDocumentClick(event) {
    if (
      this.predictiveSearch &&
      this.root.classList.contains('search-open') &&
      !event.target.closest('#HeaderPredictiveSearch') &&
      !event.target.closest('[data-action="open-search"]')
    ) {
      this.closeSearch();
    }

    if (
      this.localization &&
      this.localization.classList.contains('open') &&
      !event.target.closest('#HeaderLocalization')
    ) {
      this.localization.classList.remove('open');
    }
  }

  calcHeader() {
		const headerObserver = new ResizeObserver(() => {
			this.headerHeight = this.root.getBoundingClientRect().height;

      const announcements = document.querySelectorAll(".announcement");
      this.allAnnouncementsHeight = Math.max(Array.from(announcements).reduce((sum, el) => {
        return sum + el.offsetHeight;
      }, 0), 1);
      
      document.documentElement.style.setProperty('--menu-height', `${this.headerHeight + this.allAnnouncementsHeight}px`);
      document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);
      this.root.classList.toggle('sticky', window.scrollY >= this.allAnnouncementsHeight);
		});

		headerObserver.observe(this.root);
	}

  onClick(event) {
    const actionTarget = event.target.closest("[data-action]");
    if (!actionTarget) return;

    switch (actionTarget.dataset.action) {
      case "menu-toggle":
        this.toggleMobileMenu();
        break;
      case "submenu-toggle":
        const sub = actionTarget.nextElementSibling;

        actionTarget.classList.toggle("open");
        if (actionTarget.classList.contains("open")) {
          sub.style.maxHeight = sub.scrollHeight + "px";
          setTimeout(() => {sub.style.maxHeight = "unset"}, 300);
        } else {
          sub.style.maxHeight = sub.scrollHeight + "px";
          setTimeout(() => {sub.style.maxHeight = null}, 50);
        }
        break;
      case "localization-toggle":
        this.localization.classList.toggle("open");
        break;
      case "select-lang":
        const localizationForm = this.localization.querySelector("form");
        localizationForm.querySelector("[name=\"locale_code\"]").value = actionTarget.dataset.value;
        localizationForm.submit();
        break;
      case "open-search":
        this.openSearch();
        break;
      case "close-search":
        this.closeSearch();
        break;
    }
  }

  toggleMobileMenu() {
    if (!this.root.classList.contains("open")) {
      this.root.classList.add("open");
      document.documentElement.style.overflow = "hidden";
    } else {
      this.root.classList.remove("open");
      document.documentElement.style.overflow = null;
    }
  }

  openSearch() {
    if (!this.predictiveSearch) return;
    this.root.classList.add("search-open");
    document.documentElement.style.overflow = "hidden";
    if (this.searchInput) {
      setTimeout(() => this.searchInput.focus(), 50);
    }
  }

  closeSearch() {
    if (!this.predictiveSearch) return;
    this.root.classList.remove("search-open");
    document.documentElement.style.overflow = null;
  }

  onPredictiveChange(query) {
    if (!this.searchResults) return;

    const trimmed = query.trim();

    if (trimmed === "") {
      this.searchResults.innerHTML = "";
      return;
    }

    this.toggleLoading(true);

    const searchUrl = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(trimmed)}&resources[type]=product&section_id=predictive-results`;

    fetch(searchUrl)
      .then(response => response.ok ? response.text() : Promise.reject(response.status))
      .then(text => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const section = html.querySelector("#shopify-section-predictive-results");
        this.searchResults.innerHTML = section ? section.innerHTML : "";
      })
      .catch(error => {
        console.error(error);
        this.searchResults.innerHTML = "";
      })
      .finally(() => this.toggleLoading(false));
  }

  toggleLoading(state) {
    this.isLoading = state;
    this.root.classList.toggle("loading", state);
  }
}

if (document.querySelector("header")) {
  new Header(document.querySelector("header"))
}

// Animation functionality 

document.addEventListener('DOMContentLoaded', () => {
  initJsAnimations();
});

function initJsAnimations(userOpts = {}) {
  const opts = {
    sectionPxToTrigger: 150,
    cardThreshold: 0.2,
    sectionDuration: 0.7,
    cardDuration: 0.6,
    cardDelayStep: 0.3,
    rootMargin: '0px',
    once: true,
    ...userOpts
  };

  const sections = Array.from(document.querySelectorAll('[js-animation]'));
  if (!sections.length) return;

  sections.forEach(sec => {
    const sectionPxToTrigger = getNumber(sec.dataset.sectionPx, opts.sectionPxToTrigger);
    const sectionDuration = getNumber(sec.dataset.sectionDuration, opts.sectionDuration);
    const cardDuration = getNumber(sec.dataset.cardDuration, opts.cardDuration);
    const cardDelayStep = getNumber(sec.dataset.cardDelayStep, opts.cardDelayStep);

    sec.style.setProperty('--section-dur', `${sectionDuration}s`);
    sec.style.setProperty('--card-dur', `${cardDuration}s`);
    sec.style.setProperty('--card-delay-step', `${cardDelayStep}s`);

    // Збережемо опції секції
    sec.__animOpts = { sectionPxToTrigger, cardDelayStep };
  });

  // Спостерігач для карток лишаємо (threshold по картці як було)
  const cardObserver = new IntersectionObserver(onCardsIntersect, {
    root: null,
    rootMargin: opts.rootMargin,
    threshold: buildThresholds(opts.cardThreshold)
  });

  sections.forEach(sec => {
    getCards(sec).forEach(card => {
      const order = Number(card.getAttribute('js-animation-block')) || 0;
      card.__order = order;
      card.__animated = false;
      cardObserver.observe(card);
    });
  });

  const pending = new Set(sections);
  let ticking = false;

  function checkSections() {
    ticking = false;
    const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;

    pending.forEach(sec => {
      const rect = sec.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= viewportH) {

        if (!opts.once) {
          sec.classList.remove('is-animated');
          getCards(sec).forEach(c => {
            c.__animated = false;
            c.classList.remove('is-animated');
            c.style.transitionDelay = '';
          });
        }
        return;
      }

      const needPx = Math.min(sec.__animOpts.sectionPxToTrigger, rect.height);
      const firstChunkVisible = rect.top <= (viewportH - needPx);

      if (firstChunkVisible) {
        if (!sec.classList.contains('is-animated')) {
          sec.classList.add('is-animated');
        }
        if (opts.once) pending.delete(sec);
      } else if (!opts.once) {
        sec.classList.remove('is-animated');
        getCards(sec).forEach(c => {
          c.__animated = false;
          c.classList.remove('is-animated');
          c.style.transitionDelay = '';
        });
      }
    });
  }

  function onScrollOrResize() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(checkSections);
    }
  }

  onScrollOrResize();
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);

  let rafScheduled = false;
  const pendingVisibleBySection = new Map();

  function onCardsIntersect(entries) {
    for (const entry of entries) {
      const card = entry.target;
      const sec = findSection(card);
      if (!sec) continue;

      const isVisible = entry.isIntersecting && entry.intersectionRatio > 0;
      if (isVisible && !card.__animated) {
        if (!pendingVisibleBySection.has(sec)) pendingVisibleBySection.set(sec, new Set());
        pendingVisibleBySection.get(sec).add(card);
      }

      if (!isVisible && !opts.once && card.__animated) {
        card.__animated = false;
        card.classList.remove('is-animated');
        card.style.transitionDelay = '';
      }
    }

    if (!rafScheduled) {
      rafScheduled = true;
      requestAnimationFrame(flushVisibleBatches);
    }
  }

  function flushVisibleBatches() {
    rafScheduled = false;
    for (const [sec, setOfCards] of pendingVisibleBySection.entries()) {
      if (!sec.classList.contains('is-animated')) continue;

      const delayStep = sec.__animOpts?.cardDelayStep ?? opts.cardDelayStep;

      const batch = Array.from(setOfCards)
        .filter(c => !c.__animated)
        .sort((a, b) => (a.__order || 0) - (b.__order || 0));

      batch.forEach((card, idx) => {
        card.style.transitionDelay = `${idx * delayStep}s`;
        card.classList.add('is-animated');
        card.__animated = true;
      });

      setOfCards.clear();
    }
  }

  function getCards(sectionEl) {
    return Array.from(sectionEl.querySelectorAll('[js-animation-block]'));
  }

  function findSection(el) {
    return el.closest('[js-animation]');
  }

  function buildThresholds(minRatio) {
    const steps = 10;
    const arr = [];
    for (let i = 0; i <= steps; i++) arr.push(i / steps);
    if (!arr.includes(minRatio)) arr.push(minRatio);
    return arr.sort((a, b) => a - b);
  }

  function getNumber(val, fallback) {
    const n = Number(val);
    return Number.isFinite(n) ? n : fallback;
  }
}

class FeaturedProduct extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this.dataset.type == "recommendations") {
      this.setRecommendationProducts();
    }
  }

  setRecommendationProducts() {
    const recommendationLink = window.Shopify.routes.root + `recommendations/products?limit=4&product_id=${this.dataset.product}&intent=${this.dataset.recommendations}&section_id=${this.dataset.section}`;
      fetch(recommendationLink)
      .then(response => response.text())
      .then(response => {
        const html = new DOMParser().parseFromString(response, "text/html");

        if (html.querySelector("featured-products[data-empty]")) {
          this.style.display = "none";
          return;
        }
        
        const grid = this.querySelector('[id^="Slider-"]');
        grid.innerHTML = html.querySelector('[id^="Slider-"]').innerHTML;

        this.querySelector("slider-component").update();
      })
      .catch(error => console.error(error))
  }
}

if (document.querySelector("featured-products")) {
  customElements.define("featured-products", FeaturedProduct);
}

class MainProduct extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.cart = document.querySelector("cart-drawer");
    this.updateGallery = this.updateGallery.bind(this);
    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onStickyClick = this.onStickyClick.bind(this);
  }
  
  connectedCallback() {
    this.productVariants = JSON.parse(this.querySelector("[data-variants-data]").textContent);
    this.productGallery = this.querySelector('[id^="ProductGallery-"]');
    this.productQuantityOptions = this.querySelector('[id^="ProductQuantityOptions-"]');
    this.productForm = this.querySelector('[id^="ProductForm-"]');
    this.stickyBar = this.querySelector("[data-sticky-atc]");
    this.stickyAddButton = this.stickyBar?.querySelector('[data-action="sticky-add"]');
    this.mainSubmit = this.querySelector('[id^="ProductSubmit-"]');

    this.productGallery?.addEventListener('slider-updated', this.updateGallery);
    this.productQuantityOptions?.addEventListener("change", this.onQuantityChange);
    this.productForm?.addEventListener("submit", this.onFormSubmit);
    this.initClickListeners();
    this.initStickyBar();
  }

  disconnectedCallback() {
    this.productGallery?.removeEventListener('slider-updated', this.updateGallery);
    this.productQuantityOptions?.removeEventListener("change", this.onQuantityChange);
    this.productForm?.removeEventListener("submit", this.onFormSubmit);
    this.stickyAddButton?.removeEventListener("click", this.onStickyClick);
    this.stickyObserver?.disconnect();
  }

  onStickyClick(event) {
    if (this.stickyAddButton?.hasAttribute("data-bis")) {
      this.openBis();
      return;
    }

    this.onFormSubmit(event);
  }

  onFormSubmit(event = false) {
    if (event) event.preventDefault();

    if (this.isLoading) return;

    if (event && event.submitter?.hasAttribute("data-bis")) {
      this.openBis();
      return;
    }

    const cartData = {
      items: [
        {
          id: parseInt(this.querySelector("[name='id']").value),
          quantity: parseInt(this.querySelector("[name='quantity']").value)
        }
      ]
    };

    this.addToCart(cartData);
  }

  getSelectedDiscountCode() {
    const checkedOption = this.productQuantityOptions?.querySelector("input:checked");
    return checkedOption?.dataset.discountCode || "";
  }

  addToCart(data) {
    if (!data) return;

    this.toggleLoading();

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    .then(response => response.json())
    .then(response => {
      if (!response.items) {
        const error = new Error(response.description);
        throw error;
      } else {
        this.toggleLoading();

        if (this.cart) {
          const extraCode = this.getSelectedDiscountCode();
          const targetDiscounts = this.cart.computeTargetDiscounts({}, extraCode ? [extraCode] : []);

          return this.cart.applyDiscountsAndRender(targetDiscounts).then(() => this.cart.open());
        }
      }
    })
    .catch(error => this.showError(error))
    .finally(() => this.toggleLoading(true));
  }

  onQuantityChange() {
    const option = event.target;

    const priceEl = this.querySelector('[data-content="price"]');
    const compareEl = this.querySelector('[data-content="compare"]');
    const savingsEl = this.querySelector('[data-content="package"]');

    priceEl.textContent = option.dataset.price;
    compareEl.textContent = option.dataset.compare;
    savingsEl.textContent = option.dataset.note;
    this.querySelector("[name='quantity']").value = option.value;

    this.syncStickyBar();
  }

  initStickyBar() {
    if (!this.stickyBar || !this.mainSubmit) return;

    this.syncStickyBar();
    this.stickyAddButton?.addEventListener("click", this.onStickyClick);

    this.stickyObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.stickyBar.classList.toggle("main-product__sticky--visible", scrolledPast);
      });
    }, { threshold: 0 });

    this.stickyObserver.observe(this.mainSubmit);
  }

  syncStickyBar() {
    if (!this.stickyBar) return;

    const price = this.querySelector('[data-content="price"]')?.textContent.trim() || "";
    const compare = this.querySelector('[data-content="compare"]')?.textContent.trim() || "";

    const stickyPrice = this.stickyBar.querySelector('[data-content="sticky-price"]');
    const stickyCompare = this.stickyBar.querySelector('[data-content="sticky-compare"]');
    const stickyDiscount = this.stickyBar.querySelector('[data-content="sticky-discount"]');

    if (stickyPrice) stickyPrice.textContent = price;
    if (stickyCompare) stickyCompare.textContent = compare;

    if (stickyDiscount) {
      const percent = this.calcDiscount(price, compare);
      stickyDiscount.textContent = percent > 0 ? `-${percent}%` : "";
      stickyDiscount.hidden = percent <= 0;
    }
  }

  calcDiscount(priceStr, compareStr) {
    const toNumber = (value) => parseInt((value || "").replace(/[^\d]/g, ""), 10);
    const price = toNumber(priceStr);
    const compare = toNumber(compareStr);
    if (!price || !compare || compare <= price) return 0;
    return Math.round((1 - price / compare) * 100);
  }

  initClickListeners() {
    this.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-action")) {
        switch (e.target.dataset.action) {
          case "scroll-gallery-to":
            this.productGallery.querySelector("slider-component").scrollToSlide(e.target.dataset.slide);
            break;
          case "toggle-disclosure":
            const disclosureHandle = e.target.dataset.disclosure;
            const disclosureContent = this.querySelector(`[data-disclosure-content="${disclosureHandle}"]`);
              
            e.target.classList.toggle("open");

            if (e.target.classList.contains("open")) {
              disclosureContent.style.maxHeight = disclosureContent.scrollHeight + "px";
            } else {
              disclosureContent.style.maxHeight = null;
            }
            break;
          case "close-bis":
            this.closeBis();
            break;
        }
      }
    })
  }

  openBis() {
    const popup = this.querySelector("[id^='ProductBis-']");
    if (!popup) return;

    popup.classList.remove("success", "loading");
    popup.hidden = false;
    document.documentElement.style.overflow = "hidden";

    if (!this.bisFormBound) {
      const form = popup.querySelector("form");
      if (form) {
        form.addEventListener("submit", (e) => {
          e.preventDefault();
          this.submitBis();
        });
        this.bisFormBound = true;
      }
    }

    const emailInput = popup.querySelector("input[type='email']");
    setTimeout(() => emailInput?.focus(), 50);
  }

  closeBis() {
    const popup = this.querySelector("[id^='ProductBis-']");
    if (!popup) return;

    popup.hidden = true;
    document.documentElement.style.overflow = null;
  }

  submitBis() {
    const popup = this.querySelector("[id^='ProductBis-']");
    if (!popup || popup.classList.contains("loading")) return;

    const emailInput = popup.querySelector("input[type='email']");
    const email = emailInput?.value?.trim();
    if (!email) return;

    const accountId = popup.dataset.klaviyoAccount;
    const variantId = this.querySelector("[name='id']")?.value;
    if (!accountId || !variantId) return;

    popup.classList.add("loading");

    fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${accountId}`, {
      method: "POST",
      headers: {
        "revision": "2024-02-15",
        "Content-Type": "application/json",
        "X-Klaviyo-Onsite": "1"
      },
      body: JSON.stringify({
        data: {
          type: "back-in-stock-subscription",
          attributes: {
            profile: { data: { type: "profile", attributes: { email } } },
            channels: ["EMAIL"]
          },
          relationships: {
            variant: {
              data: {
                type: "catalog-variant",
                id: `$shopify:::$default:::${variantId}`
              }
            }
          }
        }
      })
    })
    .then((res) => {
      popup.classList.remove("loading");
      if (res.ok || res.status === 409) {
        popup.classList.add("success");
      } else {
        console.error("BIS subscribe failed:", res.status);
      }
    })
    .catch((err) => {
      popup.classList.remove("loading");
      console.error("BIS subscribe error:", err);
    });
  }

  updateGallery(event) {    
    this.querySelectorAll('[id^="Slider-ProductGalleryNav-"] li').forEach(slide => {
      const index = parseInt(slide.dataset.slide);
      slide.classList.toggle("active", event.detail.currentSlide === index);
    })
  }

  showError(error) {
    console.error(error);

    if (!this.querySelector("[product-notification]")) return;

    const messageBlock = this.querySelector("[product-notification]");

    messageBlock.innerText = error.message;
    setTimeout(() => {messageBlock.innerText = ''}, 5000);
    messageBlock.scrollIntoView({behavior: "smooth",block:"center"});
  }

  toggleLoading(turnOff = false) {
    this.isLoading = !this.isLoading;
    if (turnOff) this.isLoading = false;
    this.classList.toggle("loading", this.isLoading);
  }

  isInViewport(element) {
    const rect = element.getBoundingClientRect();

    let isInViewport = (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );

    if (rect.top > 0) isInViewport = true;
    
    return isInViewport;
  };
}

if (document.querySelector("main-product")) {
  customElements.define("main-product", MainProduct);
}

class MainCollection extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.productsGrid = this.querySelector('[id^="CollectionGrid-"]');

    this.initClickEvents();
  }

  initClickEvents() {
    this.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-action")) {
        switch (e.target.dataset.action) {
          case "switch-collection":
            this.querySelector('[data-featured] .selected')?.classList.remove("selected");
            e.target.classList.add("selected");
            this.filtersUpdate(e.target.dataset.collection);
            break;
        }
      }
    })
  }

  filtersUpdate(handle = false) {
    this.toggleLoading();
    
    let newProductUrl = "";

    if (!newProductUrl) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("sort_by", this.querySelector("[name='sort_by']").value);
      if (handle) {
        searchParams.set("page", 1);
        newProductUrl = "/collections" + `/${handle}?${searchParams.toString()}`;
      } else {
        newProductUrl = window.location.pathname + `?${searchParams.toString()}`;
      }
    }
    
    window.history.replaceState({}, '', newProductUrl);

    fetch(newProductUrl)
    .then(res => res.text())
    .then(body => {
      const html = new DOMParser().parseFromString(body, "text/html");
      this.renderContent(html);
    })
    .catch(error => console.error(error))
    .finally(() => this.toggleLoading())
  }

  renderContent(html) {
    const section = html.querySelector(`main-collection`);
    const selectors = ["[data-content]"];

    selectors.forEach(selector => {
      if (this.querySelector(selector) && section.querySelector(selector)) {
        this.querySelectorAll(selector).forEach((selectorElement, i) => {
          selectorElement.innerHTML = section.querySelectorAll(selector)[i].innerHTML;
        })
      }
    })

    this.setAttribute("data-type", section.dataset.type);
  }

  toggleLoading() {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading", this.isLoading);
  }
}

if (document.querySelector("main-collection")) {
  customElements.define("main-collection", MainCollection);
}

class MainBlog extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.initClickEvents();
  }

  initClickEvents() {
    this.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-action")) {
        switch (e.target.dataset.action) {
          case "switch-blog":
            this.querySelector('[data-tabs] .active').classList.remove("active");
            e.target.classList.add("active");
            this.switchBlog(e.target.dataset.blog);
            break;
        }
      }
    });
  }

  switchBlog(handle) {
    this.toggleLoading();

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("page", 1);
    const newBlogUrl = "/blogs/" + handle + "?" + searchParams.toString();

    window.history.replaceState({}, '', "/blogs/" + handle);

    fetch(newBlogUrl)
    .then(res => res.text())
    .then(body => {
      const html = new DOMParser().parseFromString(body, "text/html");
      this.renderContent(html);
    })
    .catch(error => console.error(error))
    .finally(() => this.toggleLoading());
  }

  renderContent(html) {
    const section = html.querySelector(`[data-section="${this.dataset.section}"]`);
    const selectors = [
      "[data-title]",
      "[data-grid]",
      "[data-pagination]"
    ];

    selectors.forEach(selector => {
      if (this.querySelector(selector) && section.querySelector(selector)) {
        this.querySelector(selector).innerHTML = section.querySelector(selector).innerHTML;
      }
    });

    if (document.querySelector(".faq__blocks") && section.querySelector(".faq__blocks")) {
      document.querySelector(".faq__blocks").innerHTML = section.querySelector(".faq__blocks").innerHTML;
    }

    if (document.querySelector(".main-video") && section.querySelector(".main-video")) {
      document.querySelector(".main-video").innerHTML = section.querySelector(".main-video").innerHTML;
      document.querySelector(".main-video").videoInit();
    }
  }

  toggleLoading() {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading", this.isLoading);
  }
}

if (document.querySelector("main-blog")) {
  customElements.define("main-blog", MainBlog);
}

class MainSearch extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;

    this.initClickEvents();
  }

  initClickEvents() {
    this.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-action")) {
        switch (e.target.dataset.action) {
          case "open-options":
            this.querySelector("[data-sort]").classList.add("open");
            break;
          case "select-option":
            this.querySelector("[name='sort_by']").value = e.target.dataset.option;
            this.filtersUpdate();
            break;
          case "clear-search":
            const input = this.querySelector(".main-search__form-input");
            input.value = "";
            input.focus();
            break;
        }
      }
    });

    window.addEventListener("click", (e) => {
      const sort = this.querySelector("[data-sort]");
      if (sort && !e.target.closest("[data-sort]")) {
        sort.classList.remove("open");
      }
    });
  }

  filtersUpdate() {
    this.toggleLoading();

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("sort_by", this.querySelector("[name='sort_by']").value);
    const newUrl = window.location.pathname + `?${searchParams.toString()}`;

    window.history.replaceState({}, "", newUrl);

    fetch(newUrl)
      .then((res) => res.text())
      .then((body) => {
        const html = new DOMParser().parseFromString(body, "text/html");
        this.renderContent(html);
      })
      .catch((error) => console.error(error))
      .finally(() => this.toggleLoading());
  }

  renderContent(html) {
    const section = html.querySelector(`[data-section="${this.dataset.section}"]`);
    const selectors = [
      "[data-headings]",
      "[data-grid]",
      "[data-pagination]",
    ];

    selectors.forEach((selector) => {
      if (this.querySelector(selector) && section.querySelector(selector)) {
        this.querySelectorAll(selector).forEach((selectorElement, i) => {
          selectorElement.innerHTML = section.querySelectorAll(selector)[i].innerHTML;
        });
      }
    });
  }

  toggleLoading() {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading", this.isLoading);
  }
}

if (document.querySelector("main-search")) {
  customElements.define("main-search", MainSearch);
}

// --- COMPONENTS --- //

class SliderComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.currentSlide = 1;
    this.allowEvents = this.getAttribute("data-allow-events") === "true";
    this.hideControls = this.getAttribute("data-hide-controls") !== "false"

    this.slider = this.querySelector('[id^="Slider-"]');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.scrollbar = this.querySelector("[slider-scrollbar]");
    this.scrollbarThumb = this.querySelector("[slider-scrollbar] span");

    this.pagination = this.querySelector('[slider-pagination]');

    this.counter = this.querySelector('[slider-counter]');

    if (!this.slider) return;

    this.update();
    window.addEventListener('resize', this.update.bind(this));
    this.slider.addEventListener('scroll', this.update.bind(this));

    if (this.prevButton && this.nextButton) {
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    if (this.pagination) {
      this.pagination.addEventListener("click", this.onDotClick.bind(this))
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    if (event.currentTarget.name === 'previous' && this.currentSlide > 1) {
      this.currentSlide -= 1;
    } else if (event.currentTarget.name === 'next' && this.currentSlide < this.maxLength) {
      this.currentSlide += 1;
    }
    this.scrollToSlide(this.currentSlide);
  }

  onDotClick(event) {
    event.preventDefault();
    if (!event.target.hasAttribute("data-slide")) return;

    const slideIndex = parseInt(event.target.dataset.slide);
    this.scrollToSlide(slideIndex);
  }

  scrollToSlide(index) {
    this.slider.scrollTo({
      left: this.querySelector(`[id^="Slide-"]:nth-child(${index})`).offsetLeft,
      behavior: 'smooth'
    });
  }

  update() {
    const items = this.querySelectorAll('[id^="Slide-"]');
    const scrollLeft = this.slider.scrollLeft;
    const scrollWidth = this.slider.scrollWidth;
    const clientWidth = this.slider.clientWidth;
    const blockWidth = items[0]?.offsetWidth ?? 0;
    const gap = window.innerWidth < 750 ? 12 : 16;
    const hasScroll = this.slider.scrollWidth > clientWidth;
      
    this.maxLength = items.length - (Math.floor(clientWidth / blockWidth) - 1);
    this.currentSlide = Math.round(scrollLeft / (blockWidth + gap)) + 1;

    items.forEach((item, index) => item.classList.toggle("active", index + 1 == this.currentSlide));
    
    if (this.prevButton && this.nextButton) {
      this.prevButton.toggleAttribute("disabled", this.currentSlide == 1);
      this.nextButton.toggleAttribute("disabled", this.currentSlide == this.maxLength);
  
      if (this.hideControls) {
        this.prevButton.classList.toggle("hidden", !hasScroll);
        this.nextButton.classList.toggle("hidden", !hasScroll);
      }
    }

    if (this.scrollbarThumb) {      
      const thumbWidth = (clientWidth / scrollWidth) * 100;
      const thumbPosition = (scrollLeft / (scrollWidth - clientWidth)) * 100;

      this.scrollbarThumb.style.left = `${thumbPosition}%`;
      this.scrollbarThumb.style.width = `${thumbWidth}%`;
      this.scrollbarThumb.style.transform = `translateX(-${thumbPosition}%)`;
      this.scrollbar.classList.toggle("hidden", !hasScroll);
    }

    if (this.pagination) {
      this.updatePagination();
    }

    if (this.counter) {
      this.counter.innerHTML = `${this.currentSlide} / ${items.length}`;
    }
  
    if (this.allowEvents) {
      this.dispatchEvent(
        new CustomEvent("slider-updated", {
          detail: {
            currentSlide: this.currentSlide
          },
          bubbles: true,
        })
      );
    }
  }

  updatePagination() {
    const slides = this.querySelectorAll('[id^="Slide-"]');

    if (!this.slider || slides.length === 0 || !this.pagination) return;

    const containerWidth = this.slider.clientWidth;
    const slideWidth = slides[0].offsetWidth;

    const slidesPerPage = Math.floor(containerWidth / slideWidth);
    const totalPages = slides.length - (slidesPerPage - 1);

    this.pagination.innerHTML = '';
    this.pagination.classList.toggle("hidden", totalPages === 1);

    for (let i = 1; i <= totalPages; i++) {
      const dot = document.createElement('span');
      dot.setAttribute("data-slide", i);
      if (i === this.currentSlide) dot.classList.add("active");
      this.pagination.appendChild(dot);
    }
  }
}

customElements.define("slider-component", SliderComponent);

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.cart = document.querySelector("cart-drawer");
    this.quickAddButton = this.querySelector("[data-quick-add]");
    this.onQuickAdd = this.onQuickAdd.bind(this);
  }

  connectedCallback() {
    this.quickAddButton.addEventListener("click", this.onQuickAdd)
  }

  onQuickAdd(event) {
    event.preventDefault();

    if (this.isLoading) return;

    this.toggleLoading();

    const sections = [];
    if (this.cart) sections.push("cart-drawer");

    const cartData = {
      items: [
        {
          id: parseInt(this.quickAddButton.dataset.quickAdd),
          quantity: 1
        }
      ],
      sections: sections,
      sections_url: window.location.pathname
    };

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cartData),
    })
    .then(response => response.json())
    .then(response => {
      if (!response.items) {
        throw new Error(response.description);
      } else {
        if (this.cart) {
          this.cart.renderContent(response.sections);
          this.cart.open();
        }
      }
    })
    .catch(error => console.error(error))
    .finally(() => this.toggleLoading());
  }

  toggleLoading() {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading", this.isLoading);
  }
}

customElements.define("product-card", ProductCard);

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;

    this.initClickEvents();
  }

  connectedCallback() {
    this.initClickEvents();
    this.syncDiscounts();

    document.addEventListener("click", (e) => {
      if (e.target.hasAttribute("cart-drawer-toggle")) {
        this.open();
      }
    })
  }

  updateQuantity(item, quantity, itemEl = false) {
    this.toggleLoading(itemEl);

    const lineItems = [...this.querySelectorAll("[data-item]")];
    const updates = lineItems.map((el) => {
      const line = parseInt(el.dataset.item);
      const currentQty = parseInt(el.querySelector('[name="quantity"]')?.value);
      return line === item ? quantity : currentQty;
    });

    const targetDiscounts = this.computeTargetDiscounts({ [item]: quantity });

    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        updates: updates,
        discount: targetDiscounts.join(","),
        sections: ["cart-drawer"],
        sections_url: window.location.pathname
      })
    })
    .then(response => response.json())
    .then(response => {
      if (!response.sections) throw new Error(response.description || "Cart update failed");

      this.renderContent(response.sections);
    })
    .catch((error) => {
      const errItemEl = this.querySelector(`[data-item="${item}"]`);
      const messageBlock = errItemEl?.querySelector("[item-notification]");

      console.error('Error:', error);

      if (messageBlock) {
        messageBlock.innerText = error.message;
        setTimeout(() => {messageBlock.innerText = ""}, 5000);
      }
    })
    .finally(() => {this.toggleLoading(itemEl)});
  }

  parseDiscountMap(value) {
    const map = {};

    (value || "")
      .split("||")
      .map((pair) => pair.trim())
      .filter(Boolean)
      .forEach((pair) => {
        const separator = pair.indexOf(":");
        if (separator === -1) return;
        const quantity = pair.slice(0, separator).trim();
        const code = pair.slice(separator + 1).trim();
        if (quantity && code) map[quantity] = code;
      });

    return map;
  }

  resolveDiscountCode(discountMap, quantity) {
    let bestQuantity = 0;
    let bestCode = "";

    Object.keys(discountMap).forEach((key) => {
      const optionQuantity = parseInt(key);
      if (!isNaN(optionQuantity) && optionQuantity <= quantity && optionQuantity > bestQuantity) {
        bestQuantity = optionQuantity;
        bestCode = discountMap[key];
      }
    });

    return bestCode;
  }

  computeTargetDiscounts(quantityOverrides = {}, extraCodes = []) {
    const managed = this.parseDiscountCodes(this.dataset.managedDiscounts);
    const applied = this.parseDiscountCodes(this.dataset.appliedDiscounts);
    const manual = applied.filter((code) => !managed.includes(code));

    const required = [];
    this.querySelectorAll("[data-item]").forEach((itemEl) => {
      const line = itemEl.dataset.item;
      const discountMap = this.parseDiscountMap(itemEl.dataset.discountMap);
      const quantity = Object.prototype.hasOwnProperty.call(quantityOverrides, line)
        ? quantityOverrides[line]
        : parseInt(itemEl.querySelector('[name="quantity"]')?.value);

      const code = this.resolveDiscountCode(discountMap, quantity);
      if (code) required.push(code);
    });

    const target = [...new Set([...manual, ...required, ...extraCodes.filter(Boolean)])];

    console.log("[Cart discounts target]", { manual, required, extraCodes, target });

    return target;
  }

  applyDiscountsAndRender(targetCodes) {
    return fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        discount: targetCodes.join(","),
        sections: ["cart-drawer"],
        sections_url: window.location.pathname
      })
    })
    .then((response) => response.json())
    .then((response) => {
      if (response.sections) this.renderContent(response.sections);
      return response;
    });
  }

  renderContent(sections) {
    if (!sections['cart-drawer']) return;

    const html = new DOMParser().parseFromString(sections['cart-drawer'], "text/html");
    const newCartDrawer = html.querySelector("cart-drawer");
    
    this.querySelector("[js-content-items]").innerHTML = newCartDrawer.querySelector("[js-content-items]").innerHTML;
    this.querySelector("[js-content-footer]").innerHTML = newCartDrawer.querySelector("[js-content-footer]").innerHTML;
    this.setAttribute("data-count", newCartDrawer.dataset.count);
    this.querySelector("[cart-count]").textContent = `(${newCartDrawer.dataset.count})`;

    if (this.querySelector('[data-progress="shipping"]')) {
      this.querySelector('[data-progress="shipping"]').style.width = newCartDrawer.querySelector('[data-progress="shipping"]').style.width;
      this.querySelector('.cart-drawer__shipping-label').innerHTML = newCartDrawer.querySelector('.cart-drawer__shipping-label').innerHTML;
    }

    document.querySelectorAll("[cart-item-count]").forEach(countEl => {
      countEl.innerText = newCartDrawer.dataset.count;
      countEl.setAttribute("cart-item-count", newCartDrawer.dataset.count)
    });

    this.dataset.requiredDiscounts = newCartDrawer.dataset.requiredDiscounts || "";
    this.dataset.managedDiscounts = newCartDrawer.dataset.managedDiscounts || "";
    this.dataset.appliedDiscounts = newCartDrawer.dataset.appliedDiscounts || "";
    this.syncDiscounts();
  }

  parseDiscountCodes(value) {
    return (value || "")
      .split(",")
      .map((code) => code.trim())
      .filter(Boolean);
  }

  syncDiscounts() {
    const required = this.parseDiscountCodes(this.dataset.requiredDiscounts);
    const managed = this.parseDiscountCodes(this.dataset.managedDiscounts);
    const applied = this.parseDiscountCodes(this.dataset.appliedDiscounts);

    const manual = applied.filter((code) => !managed.includes(code));
    const target = [...new Set([...manual, ...required])];

    const isSynced = target.length === applied.length && target.every((code) => applied.includes(code));

    console.log("[Cart discounts sync]", {
      required,
      managed,
      applied,
      manual,
      target,
      isSynced,
      action: isSynced ? "none" : `apply "${target.join(",")}"`
    });

    if (isSynced) {
      this.discountSyncAttempts = 0;
      return;
    }

    if (this.discountSyncAttempts >= 2) {
      console.warn("Cart discounts: unable to sync codes", { target, applied });
      return;
    }
    this.discountSyncAttempts = (this.discountSyncAttempts || 0) + 1;

    fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        discount: target.join(","),
        sections: ["cart-drawer"],
        sections_url: window.location.pathname
      })
    })
    .then((response) => response.json())
    .then((response) => {
      if (response.sections) this.renderContent(response.sections);
    })
    .catch((error) => console.error("Cart discounts sync error:", error));
  }

  initClickEvents() {
    this.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-action")) {
        e.preventDefault();

        const item = e.target.closest("[data-item]");
        const itemQuantityInput = item?.querySelector('[name="quantity"]') ?? false;
        const itemQuantity = parseInt(itemQuantityInput?.value) ?? false;
  
        switch (e.target.dataset.action) {
          case "close":
            this.close();
            break;
          case "decrease-quantity":          
            if (itemQuantity > 0) {
              if (!this.isLoading) {
                this.updateQuantity(parseInt(item.dataset.item), parseInt(itemQuantity - 1), item);
              }
            }
            break;
          case "increase-quantity":
            if (itemQuantity < 199) {
              if (!this.isLoading) {
                this.updateQuantity(parseInt(item.dataset.item), parseInt(itemQuantity + 1), item);
              }
            }
            break;
          case "quantity-update":
            if (!this.isLoading) {
              this.updateQuantity(parseInt(item.dataset.item), parseInt(e.target.dataset.value), item);
            }
            break;
          case "remove-item":
            if (item && !this.isLoading) {
              this.updateQuantity(parseInt(item.dataset.item), 0, item);
            }
            break;
          case "to-checkout":
            window.location.pathname = "/checkout";
            break;
        }
      }

    })
  }

  toggleLoading(itemEl) {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading-item", this.isLoading);

    if (itemEl) {
      itemEl.classList.toggle("loading", this.isLoading);
    }
  }

  open() {
    this.classList.add("open");
    document.documentElement.style.overflow = "hidden";
  }

  close() {
    this.classList.remove("open");
    document.documentElement.style.overflow = null;
  }
}

if (document.querySelector("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

class Localization extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initClickEvents();

    document.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-localization-open")) {
        this.open();
      }
    })
  }

  initClickEvents() {
    this.addEventListener("click", (e) => {
      e.preventDefault();
      const form = this.querySelector("form");
      switch (e.target.dataset.action) {
        case "close":
          form.classList.remove("open");
          this.close();
          break;
        case "toggle-options":
          form.classList.toggle("open");
          break;
        case "select-option":
          const selected = this.querySelector('[data-action="toggle-options"]');
          selected.innerHTML = e.target.innerHTML;
          form.classList.remove("open");
          form.querySelector("[name='country_code']").value = e.target.dataset.value;
          break;
        case "save":
          this.close();
          form.submit();
          break;
      }
    })
  }

  open() {
    this.classList.add("open");
    document.documentElement.style.overflow = "hidden";
  }

  close() {
    this.classList.remove("open");
    document.documentElement.style.overflow = null;
  }
}


if (document.querySelector("localization-popup")) {
  customElements.define("localization-popup", Localization);
}
